import React, { createContext, useContext, useEffect, useState } from 'react'
import { supabase } from '../supabase/supabaseClient'
import { initializeUserData, getUserProfile } from '../services/userDataService'
import { SUBSCRIPTION_TIERS, hasAccess as checkAccess } from '../config/subscriptions'

const AuthContext = createContext({})

// Affiliate ref code stored by RefCapture (localStorage primary, cookie backup).
// Both helpers are best-effort: any failure returns null / does nothing, so the
// referral can never break signup.
const getStoredRefCode = () => {
  try {
    const stored = localStorage.getItem('bc_ref')
    if (stored) {
      const { code, expires } = JSON.parse(stored)
      if (code && expires && Date.now() < expires && /^[a-z0-9_-]{3,20}$/.test(code)) {
        return code
      }
    }
  } catch (e) {
    // fall through to cookie
  }
  try {
    const match = document.cookie.match(/(?:^|;\s*)bc_ref=([a-z0-9_-]{3,20})(?:;|$)/)
    if (match) {
      return match[1]
    }
  } catch (e) {
    // ignore
  }
  return null
}

const clearStoredRefCode = () => {
  try {
    localStorage.removeItem('bc_ref')
  } catch (e) {
    // ignore
  }
  try {
    document.cookie = 'bc_ref=; path=/; max-age=0; SameSite=Lax'
  } catch (e) {
    // ignore
  }
}

// Create the user_profiles row for a session user, attaching the affiliate ref
// code if there is one. The code is read from the user's signup metadata first:
// with email confirmation on, the profile is created when they return from the
// confirmation link, by which point the browser that stored the code may be a
// different device entirely. localStorage is kept as a fallback for accounts
// created before the metadata was attached.
const provisionUserData = async (user) => {
  let refCode = null
  try {
    refCode = user?.user_metadata?.ref_code || null
  } catch (e) {
    // ignore
  }
  if (!refCode) {
    try {
      refCode = getStoredRefCode()
    } catch (e) {
      // ignore
    }
  }

  await initializeUserData(user.id, refCode)

  if (refCode) {
    clearStoredRefCode()
  }
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)
  const [subscriptionTier, setSubscriptionTier] = useState(SUBSCRIPTION_TIERS.FREE)
  const [isPastDue, setIsPastDue] = useState(false)
  const [hadTrial, setHadTrial] = useState(false)
  const [oddsFormat, setOddsFormat] = useState('decimal')
  const [isPasswordResetRequired, setIsPasswordResetRequired] = useState(false)

  // Fetch user's subscription tier from database
  const fetchSubscriptionTier = async (userId) => {
    setIsPastDue(false)
    try {
      // Get user email for lookup
      const { data: user } = await supabase.auth.getUser()

      if (!user?.user?.email) {
        return SUBSCRIPTION_TIERS.FREE
      }

      // Use RPC to query subscription data by email
      const { data: subscriptionData, error: rpcError } = await supabase
        .rpc('get_user_subscription_tier', { user_email: user.user.email })

      if (rpcError) {
        console.error('Failed to fetch subscription tier:', rpcError)
        return SUBSCRIPTION_TIERS.FREE
      }

      if (!subscriptionData || subscriptionData.length === 0) {
        return SUBSCRIPTION_TIERS.FREE
      }

      const result = subscriptionData[0]

      // Subscriptions with a failed/missing payment lose premium access immediately
      // instead of keeping it through Stripe's dunning window:
      //  - past_due / unpaid: a payment that previously succeeded has now failed
      //  - incomplete / incomplete_expired: the first payment never went through,
      //    so the user technically never paid
      // We flag it so the UI can prompt the user to update their payment method.
      const RESTRICTED_STATUSES = ['past_due', 'unpaid', 'incomplete', 'incomplete_expired']
      if (RESTRICTED_STATUSES.includes(result.subscription_status)) {
        setIsPastDue(true)
        return SUBSCRIPTION_TIERS.FREE
      }

      return result.subscription_tier || SUBSCRIPTION_TIERS.FREE

    } catch (error) {
      console.error('Error fetching subscription tier:', error)
      return SUBSCRIPTION_TIERS.FREE
    }
  }

  // Check if user has access to a feature
  const hasAccess = (feature) => {
    return checkAccess(subscriptionTier, feature)
  }

  useEffect(() => {
    const initAuth = async () => {
      try {
      // Normal session check
      const { data: { session } } = await supabase.auth.getSession()
      setUser(session?.user ?? null)

      if (session?.user) {
        try {
          await provisionUserData(session.user)
          const tier = await fetchSubscriptionTier(session.user.id)
          setSubscriptionTier(tier)
          // Fetch odds format preference + trial history
          const { data: profile } = await getUserProfile(session.user.id)
          if (profile?.odds_format) {
            setOddsFormat(profile.odds_format)
          }
          setHadTrial(!!profile?.had_trial)
        } catch (error) {
          console.error('Error loading user data:', error)
          setSubscriptionTier(SUBSCRIPTION_TIERS.FREE)
        }
      } else {
        setSubscriptionTier(SUBSCRIPTION_TIERS.FREE)
        setOddsFormat('decimal')
        setHadTrial(false)
      }

      } finally {
        // Must run whatever happened above. This is the only setLoading(false)
        // in the file, so anything that throws or stalls before it leaves the
        // app on its loading screen forever with no way back.
        setLoading(false)
      }
    }

    initAuth()

    // Auth state listener - updates user state and handles password recovery
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (event, session) => {
      setUser(session?.user ?? null)

      if (event === 'SIGNED_IN' && session?.user) {
        // Covers the confirmation link landing while the app is already open in
        // this tab: initAuth has already run, so this is the only place the
        // profile gets created. Safe to repeat, createUserProfile treats a
        // duplicate as success.
        //
        // Deferred with setTimeout, NOT awaited here: onAuthStateChange holds an
        // internal lock for the duration of its callback, and calling back into
        // supabase.* inside it deadlocks against that lock. The symptom is the
        // whole app stuck on its loading screen, because initAuth's getSession()
        // never resolves and setLoading(false) is never reached.
        const signedInUser = session.user
        setTimeout(async () => {
          try {
            await provisionUserData(signedInUser)
            const tier = await fetchSubscriptionTier(signedInUser.id)
            setSubscriptionTier(tier)
            const { data: profile } = await getUserProfile(signedInUser.id)
            if (profile?.odds_format) {
              setOddsFormat(profile.odds_format)
            }
            setHadTrial(!!profile?.had_trial)
          } catch (error) {
            console.error('Error loading user data on sign-in:', error)
          }
        }, 0)
      } else if (event === 'SIGNED_OUT') {
        setSubscriptionTier(SUBSCRIPTION_TIERS.FREE)
        setIsPastDue(false)
        setHadTrial(false)
        setIsPasswordResetRequired(false)
      } else if (event === 'PASSWORD_RECOVERY') {
        // User must change password before accessing the app
        setIsPasswordResetRequired(true)
        // Trigger password reset modal when password recovery event occurs
        window.dispatchEvent(new CustomEvent('password-recovery'))
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  const signUp = async (email, password) => {
    // Attach the affiliate ref to the signup itself; never fatal to signup.
    // This is what survives email confirmation: the profile row cannot be
    // written until the user has a session, which may be minutes later on
    // another device, so the code has to travel with the account rather than
    // sit in this browser's localStorage.
    let refCode = null
    try {
      refCode = getStoredRefCode()
    } catch (refError) {
      console.error('Failed to read ref code:', refError)
    }

    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      ...(refCode ? { options: { data: { ref_code: refCode } } } : {})
    })

    // Only provision now if sign-up returned a live session. With email
    // confirmation enabled it does not, and the profile is created instead on
    // the SIGNED_IN that follows the confirmation link.
    if (!error && data.user && data.session) {
      try {
        await provisionUserData(data.user)
      } catch (initError) {
        console.error('Failed to initialize user data on sign-up:', initError)
      }
    }

    return { data, error }
  }

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    })

    // If successful, fetch subscription tier and odds format
    if (!error && data.user) {
      try {
        await provisionUserData(data.user)
        const tier = await fetchSubscriptionTier(data.user.id)
        setSubscriptionTier(tier)
        // Fetch odds format preference + trial history
        const { data: profile } = await getUserProfile(data.user.id)
        if (profile?.odds_format) {
          setOddsFormat(profile.odds_format)
        }
        setHadTrial(!!profile?.had_trial)
      } catch (fetchError) {
        console.error('Failed to fetch subscription on sign-in:', fetchError)
        setSubscriptionTier(SUBSCRIPTION_TIERS.FREE)
      }
    }

    return { data, error }
  }

  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    return { error }
  }

  const resetPassword = async (email) => {
    // Use localhost for development, production URL for production
    const redirectUrl = process.env.NODE_ENV === 'development'
      ? window.location.origin
      : 'https://app.bookiecharity.com'

    const { data, error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl
    })
    return { data, error }
  }

  const updatePassword = async (newPassword) => {
    const { data, error } = await supabase.auth.updateUser({
      password: newPassword
    })
    // Clear password reset requirement after successful update
    if (!error) {
      setIsPasswordResetRequired(false)
    }
    return { data, error }
  }

  const value = {
    user,
    loading,
    subscriptionTier,
    isPastDue,
    hadTrial,
    oddsFormat,
    setOddsFormat,
    hasAccess,
    signUp,
    signIn,
    signOut,
    resetPassword,
    updatePassword,
    isPasswordResetRequired,
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}